import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { Button, Stack, Tooltip } from '@mui/material';
import { ImageSize, LoadingOverlay, Wrapper } from './styles';
import { imgUrlChk } from '@/utils/function';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import LikeOnIcon from '@/public/icons/like_on.svg';
import LikeOffIcon from '@/public/icons/like_off.svg';
import {
  translateComment,
  useDeleteComment,
  useLikeComment,
} from '@/hooks/apiHooks';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { ActionType } from '@/constant/button';
import ContentWithMention from './ContentWithMentions';
import ProfileCardPopper from '@/components/@IntranetLibrary/ProfileCard/ProfileCardPopper';
import usePopper from '@/hooks/usePopper';
import { Body2, Caption } from '@/components/@IntranetLibrary/Typography';
import AttachmentFile from '@/components/AttachmentFile';
import CommentInput, {
  CommentInputRef,
} from '@/components/Comment/CommentInput';
import { CommentImageUploadMode } from '@/components/LexicalEditor/CommentEditor';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { BoardDetailCommentProps } from '@/components/Board/BoardDetail/Feedback/BoardDetailComment';
import { Instance } from '@popperjs/core';
import { Language, User } from '@/types/api';
import { RemoteFile } from '@/components/file-thumbnail/utils';
import classNames from 'classnames';
import { hasTextContent } from './utils';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { t } from 'i18next';
import { AppLanguageCode, getApiLanguageCode } from '@/utils/language';

type Comment = {
  id: string;
  content: string;
  createdBy: User;
  commentGroupId: string;

  feedback: {
    agree: any[];
    agreeCount: number;
    disagree: any[];
    disagreeCount: number;
    recommend: any[];
    recommendCount: number;
    isAgree: boolean;
    isDisagree: boolean;
    isRecommend: boolean;
  };

  isDeleteEnable: boolean;

  //새로운 스펙
  attachments: RemoteFile[];
  updatedAt: string;
  isUpdated: boolean;
  isDeleted: boolean;
};

export type CommentItemProps = Pick<
  BoardDetailCommentProps,
  'inputStyle' | 'service' | 'beforeSubmit' | 'checkIsText'
> & {
  item: Comment;
  isMobile: boolean;
  onChanged?: (action: ActionType, commentId: string) => void;
  /**
   * (선택) 삭제 호출을 외부 함수로 override — 품의 영역 삭제 API 호출용.
   * 미전달 시 기본 `useDeleteComment` (portal `/api/v1/comment/{commentId}`) 사용.
   */
  deleteCommentOverride?: (commentId: string) => Promise<void>;
  /**
   * (선택) 좋아요 호출을 외부 함수로 override — 품의 영역 좋아요 API 호출용.
   * 미전달 시 기본 `useLikeComment` (portal `/api/v1/comment/feedback`) 사용.
   */
  likeCommentOverride?: (commentId: string, isLike: boolean) => Promise<void>;
  /**
   * (선택) 수정 모드 내부에서 사용할 CommentInput 의 `createCommentOverride`.
   * 일반적으로 수정은 `updateCommentOverride` 만 쓰지만, 인터페이스 일관성 위해 노출.
   */
  createCommentOverride?: (payload: {
    commentGroupId: string;
    content: string;
    mentions: string[];
    attachments: { name: string; link: string }[];
  }) => Promise<void>;
  /**
   * (선택) 수정 모드 내부에서 사용할 CommentInput 의 `updateCommentOverride`.
   * 품의 영역에서 수정 시 품의 수정 API 로 분기.
   */
  updateCommentOverride?: (payload: {
    commentId: string;
    content: string;
    mentions: string[];
    attachments: { name: string; link: string }[];
  }) => Promise<void>;
  /**
   * (선택) 수정 모드 CommentInput 의 이미지 첨부 방식. 미전달 시 'file'(첨부 카드).
   * 품의 영역은 'inline' 전달 → 수정 시에도 이미지 인라인(작성 모드와 동일).
   */
  imageUploadMode?: CommentImageUploadMode;
  /**
   * (선택) 전자결재 코멘트 여부. 미전달 시 false(게시판 등 기존). 품의 영역은 true 전달
   * 읽기(ContentWithMention 팀명 포함)·수정(CommentInput) 둘 다 레거시 호환 포맷.
   */
  isApproval?: boolean;
  /**
   * (선택) 인라인 수정 모드 진입/종료 통지 콜백. commentId + 현재 편집 중 여부 전달.
   * 미전달 시 호출만 생략되어서 게시판/소셜 등 기존과 동일하게 동작.
   */
  onEditModeChange?: (commentId: string, isEditing: boolean) => void;
  /**
   * (선택) 액션 버튼(수정/삭제) 하단의 첨부파일 다운로드 목록을 숨김. 미전달 시 false(표시)
   * 품의 영역은 첨부가 본문(content)에 인라인으로 포함되므로 하단 중복 다운로드 목록을 끄기 위해 true 전달.
   */
  hideAttachmentList?: boolean;
};

const CommentItem = forwardRef<CommentInputRef, CommentItemProps>(
  (props, ref) => {
    const {
      item: {
        id: commentId,
        attachments = [],
        feedback: {
          isRecommend: isLiked,
          recommendCount: likeCount,
          recommend,
        },
        content,
        createdBy: { passportId, positionName, name, profileImageUrl },
        isUpdated,
        isDeleted,
        updatedAt,
        isDeleteEnable,
        commentGroupId,
      },
      isMobile,
      onChanged,
      inputStyle,
      service,
      beforeSubmit,
      checkIsText,
      deleteCommentOverride,
      likeCommentOverride,
      createCommentOverride,
      updateCommentOverride,
      imageUploadMode,
      isApproval,
      onEditModeChange,
      hideAttachmentList,
    } = props;

    const dialog = useDialog();
    const { showSuccess, showErrorMessage } = useSnackbar();

    const { anchorEl, openPopper, closePopper } = usePopper<HTMLImageElement>();

    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const isDisabled = isDeleting || isLoading;

    const [mode, setMode] = useState<'read' | 'edit'>('read');

    // 인라인 수정 모드 진입/종료(취소·저장·언마운트 포함) 통지
    useEffect(() => {
      if (mode !== 'edit') return;
      onEditModeChange?.(commentId, true);
      return () => onEditModeChange?.(commentId, false);
    }, [mode, commentId, onEditModeChange]);

    // 번역 관련 상태
    const myInfo = useAtomValue(MyInfoState);
    const [translationState, setTranslationState] = useState<{
      status: 'idle' | 'loading' | 'translated';
      translatedContent: string | null;
      showingOriginal: boolean;
    }>({
      status: 'idle',
      translatedContent: null,
      showingOriginal: false,
    });

    const popperRef = React.useRef<Instance>(null);
    const handleMouseMove = () => {
      if (popperRef.current != null) {
        popperRef.current.update();
      }
    };

    const openUserPopper = (
      event: React.MouseEvent<HTMLImageElement, MouseEvent>,
    ) => {
      openPopper(event);
    };

    const closeUserPopper = () => {
      closePopper();
    };

    const toggleCommentLike = async (commentId: string, isLike: boolean) => {
      try {
        setIsLoading(true);
        if (likeCommentOverride) {
          await likeCommentOverride(commentId, isLike);
        } else {
          await useLikeComment(commentId, isLike, service);
        }
        if (onChanged) {
          onChanged(ActionType.LIKE, commentId);
        }
      } catch (e) {
        showErrorMessage(e);
      } finally {
        setIsLoading(false);
      }
    };

    const deleteButtonOnClick = () => {
      dialog.show({
        message: t('코멘트를 삭제하시겠어요?'),
        cancelButtonText: t('취소'),
        confirmButtonText: t('삭제'),
        forceToFront: true,
        onConfirm: async () => {
          try {
            setIsDeleting(true);
            if (deleteCommentOverride) {
              await deleteCommentOverride(commentId);
            } else {
              await useDeleteComment(commentId);
            }
            showSuccess({
              message: t('코멘트가 삭제되었습니다.'),
            });
            if (onChanged) {
              onChanged(ActionType.DELETE, commentId);
            }
          } catch (e) {
            showErrorMessage(e);
          } finally {
            setIsDeleting(false);
          }
        },
      });
    };

    const editButtonOnClick = () => {
      setMode('edit');
    };

    const likeButtonOnClick = async () => {
      try {
        await toggleCommentLike(commentId, !isLiked);
      } catch (e) {
        showErrorMessage({ e });
      }
    };

    const onEditSuccess = async () => {
      setMode('read');
      if (onChanged) {
        onChanged(ActionType.UPDATE, commentId);
      }
    };

    // 번역 버튼 노출 조건 체크
    const shouldShowTranslation = useMemo(() => {
      // 1. 내가 작성한 코멘트라면 숨김
      if (passportId === myInfo.passportId) return false;

      // 2. 텍스트가 없으면 숨김
      if (!hasTextContent(content)) return false;

      return true;
    }, [passportId, myInfo.passportId, content]);

    // 번역 버튼 텍스트 결정
    const getTranslateButtonText = () => {
      const languageLabel = Language[myInfo.language || 'ko'];

      if (
        translationState.status === 'translated' &&
        !translationState.showingOriginal
      ) {
        return t('원문보기');
      }

      return t('Comment.{{xx}}로 번역', { xx: languageLabel });
    };

    // 번역 클릭 핸들러
    const handleTranslateClick = async () => {
      // Case 1: 번역된 상태에서 "원문 보기" / "번역 보기" 토글
      if (translationState.status === 'translated') {
        setTranslationState((prev) => ({
          ...prev,
          showingOriginal: !prev.showingOriginal,
        }));
        return;
      }

      // Case 2: 최초 번역 요청
      setTranslationState({
        status: 'loading',
        translatedContent: null,
        showingOriginal: false,
      });
      setIsLoading(true);

      try {
        const currLang = (myInfo.language || 'ko') as AppLanguageCode;
        const result = await translateComment({
          commentId,
          targetLanguage: getApiLanguageCode(currLang),
        });

        setTranslationState({
          status: 'translated',
          translatedContent: result.data.translatedContent,
          showingOriginal: false,
        });
      } catch (e: any) {
        showErrorMessage(e);
        setTranslationState({
          status: 'idle',
          translatedContent: null,
          showingOriginal: false,
        });
      } finally {
        setIsLoading(false);
      }
    };

    // 렌더링할 내용 결정 (원문 or 번역)
    const displayContent =
      translationState.status === 'translated' &&
      !translationState.showingOriginal
        ? translationState.translatedContent || content
        : content;

    return (
      <>
        <Wrapper className={classNames(isMobile)}>
          {isDeleting && (
            <LoadingOverlay>
              <Spinner theme={'black'} />
            </LoadingOverlay>
          )}
          <Stack
            direction={'row'}
            sx={{ alignItems: 'center', marginBottom: '12px' }}
          >
            <ImageWithErrorHandling
              src={imgUrlChk(profileImageUrl || '')}
              width={ImageSize}
              height={ImageSize}
              alt={'profile-image'}
              className={'profile-image'}
              onClick={openUserPopper}
            />
            <Body2 className={'name-position'}>
              {name} {positionName}
            </Body2>
            <Body2 className={'createdAt'}>
              {isUpdated ? `${t('수정됨')} • ` : ''}
              {updatedAt}
            </Body2>
          </Stack>
          {isDeleted ? (
            <Stack className={'list-wrapper'}>
              <Caption>{t('삭제된 코멘트입니다.')}</Caption>
            </Stack>
          ) : (
            <Stack className={'list-wrapper'}>
              {/* 읽기 */}
              {mode === 'read' && (
                <>
                  <ContentWithMention
                    content={displayContent}
                    checkIsText={checkIsText}
                    isApproval={isApproval}
                  />
                  <Stack
                    direction={'row'}
                    sx={{
                      marginTop: '12px',
                      gap: '12px',
                      alignItems: 'center',
                    }}
                  >
                    <Tooltip
                      title={
                        <>
                          {t('Comment.{{xx}}님이 좋아합니다.', {
                            xx: recommend
                              .map((item) => [
                                `${item.name} ${item.positionName}`,
                              ])
                              .join(', '),
                          })}
                        </>
                      }
                      disableHoverListener={likeCount === 0}
                      placement="top"
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: '#000',
                            color: '#fff',
                            fontSize: '14px',
                            lineHeight: '24px',
                            fontWeight: 400,
                            padding: '8px 12px',
                            borderRadius: '12px',
                            maxWidth: '300px',
                          },
                        },
                        arrow: {
                          sx: {
                            color: 'var(--dk-text, #000)',
                          },
                        },
                      }}
                    >
                      <Button
                        disabled={isDisabled}
                        className={classNames({
                          like: true,
                          isLiked,
                        })}
                        disableRipple
                        onClick={likeButtonOnClick}
                        onMouseMove={handleMouseMove}
                      >
                        {isLiked ? (
                          <LikeOnIcon width={20} height={20} fill={'#0066CC'} />
                        ) : (
                          <LikeOffIcon width={20} height={20} />
                        )}
                        {likeCount > 0 && (
                          <Body2 className={'likeCount'}>{likeCount}</Body2>
                        )}
                      </Button>
                    </Tooltip>

                    {/* 번역 버튼 */}
                    {shouldShowTranslation &&
                      (translationState.status === 'loading' ? (
                        <Body2 className={'translating-text'}>
                          {t('번역중...')}
                        </Body2>
                      ) : (
                        <Button
                          disabled={isDisabled}
                          className={'translate'}
                          disableRipple
                          onClick={handleTranslateClick}
                        >
                          <Body2 className={'translate-text'}>
                            {getTranslateButtonText()}
                          </Body2>
                        </Button>
                      ))}

                    {/* 수정 & 삭제 버튼 */}
                    {isDeleteEnable && (
                      <>
                        <Button
                          disabled={isDisabled}
                          onClick={editButtonOnClick}
                          className={'line'}
                        >
                          <Body2 className={'line-text'}>{t('수정')}</Body2>
                        </Button>
                        <Button
                          disabled={isDisabled}
                          onClick={deleteButtonOnClick}
                          className={'line'}
                        >
                          <Body2 className={'line-text'}>{t('삭제')}</Body2>
                        </Button>
                      </>
                    )}
                  </Stack>
                </>
              )}

              {/* 수정 할 경우 */}
              {mode === 'edit' && (
                <CommentInput
                  inputStyle={inputStyle}
                  folding={false}
                  commentGroupId={commentGroupId}
                  commentId={commentId}
                  initialData={{
                    content,
                    attachments,
                  }}
                  onSuccess={onEditSuccess}
                  onCancel={onEditSuccess}
                  disabled={isDisabled}
                  service={service}
                  beforeSubmit={beforeSubmit}
                  createCommentOverride={createCommentOverride}
                  updateCommentOverride={updateCommentOverride}
                  imageUploadMode={imageUploadMode}
                  isApproval={isApproval}
                  ref={ref}
                />
              )}

              {!hideAttachmentList && attachments.length > 0 && (
                <Stack className={'attachment-wrapper'}>
                  {attachments.map((item, index) => (
                    <AttachmentFile
                      key={index}
                      file={item}
                      isDeletable={false}
                      isPreviewable={true}
                      onSuccess={() =>
                        showSuccess({
                          message: t('파일을 다운로드 했습니다.'),
                        })
                      }
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </Wrapper>

        <ProfileCardPopper
          passportId={passportId}
          anchorEl={anchorEl}
          onClose={closeUserPopper}
        />
      </>
    );
  },
);
CommentItem.displayName = 'CommentItem';
export default CommentItem;
